import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, Award, TrendingUp, ArrowLeft, Loader2, Users, ChevronLeft, ChevronRight, Search, Bookmark, BookmarkCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVATAR_COLORS = [
  'bg-[#B91C1C]', 'bg-[#0A0A0A]', 'bg-[#7F1D1D]', 'bg-[#171717]',
  'bg-[#9A3412]', 'bg-[#1E3A8A]', 'bg-[#065F46]', 'bg-[#5B21B6]'
];

const avatarSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

const DeveloperCard = ({ p, idx, navigate, isCompany, shortlisted, onToggleShortlist, busy }) => {
  const clickable = !!p.profile_url;
  const open = () => clickable && navigate(p.profile_url);
  const src = avatarSrc(p.avatar_url);
  return (
    <div
      onClick={open}
      onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(); } }}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `View ${p.name}'s profile` : undefined}
      className={`group rounded-[20px] border border-[#E5E5E5] bg-white p-6 transition-all duration-300 hover:border-[#B91C1C]/40 hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.10)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B91C1C] ${clickable ? 'cursor-pointer' : ''}`}
      data-testid={`verified-dev-card-${idx}`}
    >
      <div className="flex items-center gap-4 mb-5">
        {src ? (
          <img
            src={src}
            alt={p.name}
            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
            className="w-14 h-14 rounded-full object-cover shrink-0"
          />
        ) : null}
        <div
          className={`w-14 h-14 rounded-full ${AVATAR_COLORS[idx % AVATAR_COLORS.length]} items-center justify-center text-white font-extrabold text-[17px] shrink-0`}
          style={{ display: src ? 'none' : 'flex' }}
        >
          {p.initials}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-extrabold text-[#0A0A0A] text-[16px] truncate">{p.name}</p>
            <Shield size={15} className="text-[#B91C1C] shrink-0" fill="currentColor" />
          </div>
          {p.college && <p className="text-[13px] text-[#737373] truncate">{p.college}</p>}
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#B91C1C] bg-[#FEF2F2] px-2.5 py-1 rounded-full">
          <Award size={13} /> {p.plan_name}
        </span>
        {p.national_rank ? (
          <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[#171717]">
            <TrendingUp size={13} className="text-[#B91C1C]" /> Rank #{p.national_rank}
          </span>
        ) : (
          <span className="text-[13px] font-bold text-[#171717]">Score {p.best_score}</span>
        )}
      </div>
      {p.skills && p.skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {p.skills.map((s, i) => (
            <span key={i} className="text-[11px] font-medium text-[#525252] bg-[#FAFAF9] border border-[#F0F0F0] px-2 py-0.5 rounded-md">{s}</span>
          ))}
        </div>
      )}
      {isCompany && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleShortlist(p); }}
          disabled={busy}
          className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-bold text-[13px] transition-all disabled:opacity-60 ${
            shortlisted
              ? 'bg-[#FEF2F2] text-[#B91C1C] border border-[#B91C1C]/30 hover:bg-[#FEE2E2]'
              : 'bg-[#171717] text-white hover:bg-[#0A0A0A]'
          }`}
          data-testid={`shortlist-btn-${idx}`}
        >
          {shortlisted ? <><BookmarkCheck size={15} /> Shortlisted</> : <><Bookmark size={15} /> Shortlist</>}
        </button>
      )}
    </div>
  );
};

const PER_PAGE = 24;
const companyToken = () => localStorage.getItem('company_token');

const VerifiedDevelopersPage = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sort, setSort] = useState('rank');
  const [skill, setSkill] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const [isCompany, setIsCompany] = useState(!!companyToken());
  const [shortlistedIds, setShortlistedIds] = useState(new Set());
  const [shortlistBusy, setShortlistBusy] = useState('');

  // Debounce name search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setQ(queryInput.trim()); }, 400);
    return () => clearTimeout(t);
  }, [queryInput]);

  const fetchProfiles = async () => {
    setLoading(true);
    setError(false);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE), sort });
      if (skill) params.set('skill', skill);
      if (q) params.set('q', q);
      const res = await axios.get(`${API}/public/verified-talent/directory?${params.toString()}`);
      setProfiles(res.data.profiles || []);
      setTotalPages(res.data.total_pages || 0);
      setTotal(res.data.total || 0);
      if (res.data.available_skills && res.data.available_skills.length) {
        setAvailableSkills(res.data.available_skills);
      }
    } catch {
      setError(true);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchShortlist = async () => {
    if (!companyToken()) return;
    try {
      const res = await axios.get(`${API}/company/talent-shortlist`, {
        headers: { Authorization: `Bearer ${companyToken()}` }
      });
      setShortlistedIds(new Set(res.data.student_ids || []));
      setIsCompany(true);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        // Invalid/expired company session — don't expose recruiter controls
        localStorage.removeItem('company_token');
        localStorage.removeItem('company_data');
        setIsCompany(false);
        setShortlistedIds(new Set());
      }
    }
  };

  const toggleShortlist = async (p) => {
    const id = p.student_id;
    const isIn = shortlistedIds.has(id);
    setShortlistBusy(id);
    try {
      if (isIn) {
        await axios.delete(`${API}/company/talent-shortlist/${id}`, {
          headers: { Authorization: `Bearer ${companyToken()}` }
        });
        setShortlistedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
        toast.success(`${p.name} removed from shortlist`);
      } else {
        await axios.post(`${API}/company/talent-shortlist`, { student_id: id }, {
          headers: { Authorization: `Bearer ${companyToken()}` }
        });
        setShortlistedIds((prev) => new Set(prev).add(id));
        toast.success(`${p.name} added to your shortlist`);
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.removeItem('company_token');
        localStorage.removeItem('company_data');
        setIsCompany(false);
        toast.error('Your company session expired. Please log in again.');
      } else {
        toast.error('Could not update shortlist');
      }
    } finally {
      setShortlistBusy('');
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, [page, sort, skill, q]);

  useEffect(() => {
    fetchShortlist();
  }, []);

  const changeSort = (v) => { setPage(1); setSort(v); };
  const changeSkill = (v) => { setPage(1); setSkill(v); };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="verified-developers-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-[13px] font-bold text-[#737373] hover:text-[#B91C1C] transition-colors mb-8"
              data-testid="back-home-btn"
            >
              <ArrowLeft size={16} /> Back to Home
            </button>
          </div>
          <span className="flex w-fit items-center gap-2 text-[12px] font-bold uppercase tracking-[0.15em] text-[#B91C1C] mb-4">
            <Shield size={14} fill="currentColor" /> Verified Talent Directory
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-[#0A0A0A] leading-tight max-w-3xl">
            Browse verified developers
          </h1>
          <p className="text-lg text-[#525252] font-medium mt-4 max-w-2xl">
            Every developer here earned their EvalentX verified badge through real, proctored assessments.
          </p>

          {/* Controls */}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <input
                type="text"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                placeholder="Search by name"
                className="w-full pl-11 pr-4 py-2.5 rounded-full border border-[#E5E5E5] text-[14px] text-[#171717] focus:outline-none focus:border-[#B91C1C] transition-colors"
                data-testid="name-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[13px] font-bold text-[#525252]" htmlFor="sort-select">Sort by</label>
              <select
                id="sort-select"
                value={sort}
                onChange={(e) => changeSort(e.target.value)}
                className="px-4 py-2.5 rounded-full border border-[#E5E5E5] text-[14px] font-medium text-[#171717] bg-white focus:outline-none focus:border-[#B91C1C] cursor-pointer"
                data-testid="sort-select"
              >
                <option value="rank">National rank</option>
                <option value="score">Verified score</option>
                <option value="recent">Recently verified</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[13px] font-bold text-[#525252]" htmlFor="skill-select">Skill</label>
              <select
                id="skill-select"
                value={skill}
                onChange={(e) => changeSkill(e.target.value)}
                className="px-4 py-2.5 rounded-full border border-[#E5E5E5] text-[14px] font-medium text-[#171717] bg-white focus:outline-none focus:border-[#B91C1C] cursor-pointer"
                data-testid="skill-select"
              >
                <option value="">All skills</option>
                {availableSkills.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#737373]" data-testid="verified-dev-loading">
            <Loader2 size={28} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="verified-dev-error">
            <div className="w-16 h-16 rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center mb-6">
              <Users size={26} className="text-[#B91C1C]" />
            </div>
            <p className="text-lg font-bold text-[#171717] mb-1">Couldn't load verified developers</p>
            <p className="text-[#737373] max-w-sm mb-6">Something went wrong. Please try again.</p>
            <button
              onClick={fetchProfiles}
              className="inline-flex items-center gap-2 bg-[#171717] text-white px-6 py-3 rounded-full font-bold text-[14px] hover:bg-[#0A0A0A] transition-all"
              data-testid="verified-dev-retry"
            >
              Retry
            </button>
          </div>
        ) : profiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="verified-dev-empty">
            <div className="w-16 h-16 rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center mb-6">
              <Users size={26} className="text-[#A3A3A3]" />
            </div>
            <p className="text-lg font-bold text-[#171717] mb-1">
              {(skill || q) ? 'No matches found' : 'No verified developers yet'}
            </p>
            <p className="text-[#737373] max-w-sm">
              {(skill || q) ? 'Try a different name or skill, or clear the filters.' : 'Check back soon — developers are getting verified every week.'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-[13px] font-bold uppercase tracking-wider text-[#525252] mb-8" data-testid="verified-dev-count">
              {total} verified developer{total === 1 ? '' : 's'}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {profiles.map((p, i) => (
                <DeveloperCard
                  key={p.student_id}
                  p={p}
                  idx={i}
                  navigate={navigate}
                  isCompany={isCompany}
                  shortlisted={shortlistedIds.has(p.student_id)}
                  onToggleShortlist={toggleShortlist}
                  busy={shortlistBusy === p.student_id}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-12" data-testid="verified-dev-pagination">
                <button
                  onClick={() => setPage((v) => Math.max(1, v - 1))}
                  disabled={page <= 1}
                  className="w-11 h-11 rounded-full border border-[#E5E5E5] flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#B91C1C] hover:text-[#B91C1C]"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-[14px] font-bold text-[#171717]" data-testid="pagination-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((v) => Math.min(totalPages, v + 1))}
                  disabled={page >= totalPages}
                  className="w-11 h-11 rounded-full border border-[#E5E5E5] flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#B91C1C] hover:text-[#B91C1C]"
                  data-testid="pagination-next"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VerifiedDevelopersPage;
